import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  useContext,
  useState,
  SyntheticEvent,
  useEffect,
} from 'react'
import { useSelector } from '@xstate/react'

import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import { getCustomerInfo } from 'src/helpers/lmnHelpers'
import LmnBackButton from 'src/components/Dialogs/Lmn/common/LmnBackButton'
import styles from 'src/components/Lmn/Lmn.scss'
import { CDN_PATH } from 'src/constants/global'
import mixpanel from 'mixpanel-browser'

const LmnHonestyAndPrivacy = (): JSX.Element => {
  const { lmnContextService } = useContext(LmnContext)
  const { send } = lmnContextService
  const { palette } = useTheme()

  const {
    isSmsTelehealthConsentChecked,
    isTelehealthAndPrivacyConsentChecked,
  } = useSelector(lmnContextService, getCustomerInfo)
  const [honestyPrivacyError, setHonestyPrivacyError] = useState(false)
  const [honestyPrivacyErrorMessage, setHonestyPrivacyErrorMessage] = useState('')

  useEffect(() => {
    mixpanel.track('view_lmn_honesty_and_privacy')
  }, [])

  const handleCheckboxChange = (
    _event: SyntheticEvent<Element, Event>,
    value: boolean,
    field: string,
  ): void => {
    send({
      type: 'SET_CUSTOMER_INFO',
      data: {
        [field]: value,
      },
    })
  }

  const handleNextStepClicked = (): void => {
    if (!isSmsTelehealthConsentChecked || !isTelehealthAndPrivacyConsentChecked) {
      setHonestyPrivacyError(true)
      setHonestyPrivacyErrorMessage('You must agree to the above policies to continue. If you do not agree to them, please do not proceed.')
      return
    }
    setHonestyPrivacyError(false)
    setHonestyPrivacyErrorMessage('')
    send('NEXT_LMN_STEP')
    mixpanel.track('completed_lmn_honesty_and_privacy')
  }

  return (
    <Box display="flex" flexDirection="column" gap="10px" alignItems="center" paddingTop="10px">
      <img src={`${CDN_PATH}/images/asset-lmn-honesty-privacy.svg`} alt="Heart with check" height="44" />
      <Typography variant="h4Bold" color="primary.main" marginTop="10px">
        Telehealth Consent & Privacy
      </Typography>
      <Typography variant="body3" color="gray1.main" textAlign="center">
        To start your consultation,  you will complete a brief medical intake form.
        The answers you provide will be kept confidential, and only be shared by our
        healthcare provider to evaluate your LMN eligibility. After you submit your
        intake form, one of our certified healthcare providers will reach out to
        you via text message to start a consultation.
      </Typography>
      <Typography variant="body3" color="gray1.main" textAlign="center">
        By starting a consultation, you consent to our
        <Link
          className={styles.lmnTermsLink}
          href="https://www.sikahealth.com/telehealth-consent"
          color="primary"
          variant="body3"
          rel="noopener noreferrer"
          target="_blank"
        >
          Telehealth Consent Policy
        </Link>
        .
      </Typography>
      <Divider sx={{ marginY: '8px' }} />
      <FormControl
        error={honestyPrivacyError}
        component="fieldset"
        variant="standard"
      >
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={isSmsTelehealthConsentChecked}
              onChange={(event, value) => handleCheckboxChange(event, value, 'isSmsTelehealthConsentChecked')}
              sx={{
                ...((honestyPrivacyError && !isSmsTelehealthConsentChecked)
                  && { color: palette.error.main }
                ),
              }}
            />
          )}
          label={(
            <Typography variant="body3">
              I agree to receiving SMS text messages for the telehealth consultation.
            </Typography>
          )}
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={isTelehealthAndPrivacyConsentChecked}
              onChange={(event, value) => handleCheckboxChange(event, value, 'isTelehealthAndPrivacyConsentChecked')}
              sx={{
                ...((honestyPrivacyError && !isTelehealthAndPrivacyConsentChecked)
                  && { color: palette.error.main }
                ),
              }}
            />
          )}
          label={(
            <Typography variant="body3">
              I agree to the
              <Link
                className={styles.lmnTermsLink}
                href="https://www.sikahealth.com/telehealth-consent"
                color="primary"
                variant="body3"
                rel="noopener noreferrer"
                target="_blank"
              >
                Telehealth Consent Policy
              </Link>
              .
            </Typography>
          )}
        />
        {honestyPrivacyError ? (
          <FormHelperText
            sx={{
              fontSize: '14px',
              marginLeft: 0,
              marginRight: 0,
            }}
          >
            {honestyPrivacyErrorMessage}
          </FormHelperText>
        ) : null}
      </FormControl>
      <Button
        className={styles.lmnButton}
        color="primary"
        fullWidth
        size="large"
        variant="contained"
        onClick={handleNextStepClicked}
      >
        <Typography variant="body2Bold">
          Continue
        </Typography>
      </Button>
      <LmnBackButton />
    </Box>
  )
}

export default LmnHonestyAndPrivacy
